import _ from "lodash";
import {
  MediaCard,
  Dropdown,
  DropdownMenu,
  IconButton,
  Tooltip,
  Button,
} from "@toddle-edu/ds-web";
import React, { useState, useRef, useMemo, useCallback } from "react";
import { compose } from "lib/apolloClient";
import {
  DownloadOutlined,
  DotsVerticalOutlined,
  DeleteOutlined,
  PencilLineOutlined,
  WarningTriangleOutlined,
  LockOpenOutlined,
  LockOutlined,
  DotsSixVerticalOutlined,
  WarningTriangleFilled,
  CloseOutlined,
  InformationOutlined,
  ArrowOutOutlined,
} from "@toddle-edu/ds-icons";
import I18nHOC from "UIComponents/I18nHOC";
import LinkWithTooltip from "UIComponents/LinkWithTooltip";
import AudioWaveformPlayer from "UIComponents/AudioWaveformPlayer";
import LTIComponent from "AppComponents/LTIComponent";
import { downloadFromUrl, openLinkInNewTab } from "modules/Services";
import { connect } from "react-redux";
import {
  getThumbUrl,
  htmlToText,
  formatBytes,
  getThumbnailIcon,
  getMediaCardFileType,
} from "Utils";
import { ModelContent } from "../AttachmentItem/AttachmentItem";
import { DEFAULT_LINK_THUMB_URL } from "store/static";
import PropTypes from "prop-types";
import classes from "./MediaCardWrapper.scss";
import classNames from "classnames";
import ACLStore from "lib/aclStore";
import moment from "moment";
import {
  getFilteredAttachment,
  getLtiToolDisplayName,
  isAttachmentLocked,
} from "Utils/AttachmentHelpers";
import {
  STUDENT,
  ENTITY_LOCKED_STATE,
  ENTITY_UNLOCKED_STATE,
} from "Constants/stringConstants";
import { getUserType } from "modules/CommonSelectors";
import LockingStateLabelComponent from "../components/LockingStateLabelComponent";
import { getUserInfo } from "Login/modules/LoginModule/LoginUtils";
import { ArrowContainer, Popover } from "@toddle-edu/react-tiny-popover";
import { colors } from "Constants";
import ConditionalWrapper from "Utils/ConditionalWrapper";
import { PROJECT_GROUP_SUBMISSION_ITEM_TEMPLATE } from "Projects/Constants/stringConstants";
import ToddleDoc from "AppComponents/ToddleDoc";
import { downloadToddleDoc } from "AppComponents/ToddleDoc/Modules/ToddleDocModule";
import { useToddleDocDownloadOptions } from "AppComponents/ToddleDoc/components/ToddleDocDownloadMenu";

const getOptionsWithIcon = ({
  t,
  size = "xx-small",
  variant = "default",
  allowDownload,
  canDeleteAttachment,
  canRenameAttachment,
  allowLockAttachment,
  allowUnlockAttachment,
  disableRename,
  disableActions,
  isLTITool,
  showAttachmentInfo,
  extraDropdownOptions,
  canRemoveAttachment,
  attachmentType,
  showNewTabOption,
  isSinglePageAndNewUnitView,
}) => {
  const canOpenInNewTab = attachmentType !== "WORKBOOK";
  const isNote = attachmentType === "NOTE";
  const optionsWithIcon = [
    ...(isNote && !_.isEmpty(extraDropdownOptions) ? extraDropdownOptions : []),
    {
      key: "lock",
      label: t("common:lock"),
      icon: (
        <LockOutlined size={size} variant={variant} disabled={disableActions} />
      ),
      isVisible: allowLockAttachment,
    },
    {
      key: "unlock",
      label: t("common:unlock"),
      icon: (
        <LockOpenOutlined
          size={size}
          variant={variant}
          disabled={disableActions}
        />
      ),
      isVisible: allowUnlockAttachment,
    },
    {
      key: "open_in_new_tab",
      label: t("common:open_in_new_tab"),
      icon: <ArrowOutOutlined size={size} variant={variant} />,
      isVisible: showNewTabOption && canOpenInNewTab,
    },
    {
      key: "download",
      label: t("common:download"),
      icon: (
        <DownloadOutlined
          size={size}
          variant={variant}
          disabled={disableActions}
        />
      ),
      isVisible: allowDownload,
    },
    {
      key: "rename",
      label: t("common:rename"),
      icon: <PencilLineOutlined disabled={disableActions} />,
      isVisible: canRenameAttachment && attachmentType !== "NOTE",
      disabled: disableRename,
    },
    {
      key: "submission_details",
      label: t("project:get_submission_details"),
      icon: <InformationOutlined />,
      isVisible: showAttachmentInfo,
    },
    {
      key: "delete",
      isDestructive: !isSinglePageAndNewUnitView,
      label:
        isLTITool || canRemoveAttachment
          ? t("common:remove")
          : t("common:delete"),
      icon: (
        <DeleteOutlined
          size={size}
          variant={isSinglePageAndNewUnitView ? "subtle" : variant}
          disabled={disableActions}
        />
      ),
      isVisible: canDeleteAttachment,
    },
    ...(!isNote && !_.isEmpty(extraDropdownOptions)
      ? extraDropdownOptions
      : []),
  ];

  return optionsWithIcon;
};

const getAllowDownload = ({ type, parentType }) => {
  const isAllowDownloadType = !["WORKBOOK", "LINK"].includes(type);

  // dont allow donwload for this type
  if (parentType === PROJECT_GROUP_SUBMISSION_ITEM_TEMPLATE) {
    return false;
  }
  return isAllowDownloadType;
};

const toolTipStyle = { maxWidth: 200, padding: 0 };

const popOverContainerStyle = { zIndex: 1000 };

const getSubtitleSuffixComponent = ({
  attachment,
  assessmentMode,
  sourceType,
  t,
}) => {
  const attachmentType = _.get(attachment, "type", "");
  if (_.isEqual(attachmentType, "WORKBOOK")) {
    if (
      _.isEqual(assessmentMode, "create") &&
      _.isEqual(sourceType, "STUDENT_TEMPLATE") &&
      _.get(attachment, "metadata.isPdfTruncated", false)
    ) {
      return (
        <Tooltip
          placement={"bottom"}
          tooltip={t("toastMsgs:wrbk_max_number_of_pages_reached", {
            count: ACLStore.can("FeatureFlag:WorkbookMax20Pages") ? 20 : 15,
          })}
          showArrow={true}
        >
          <WarningTriangleOutlined variant="subtle" size="xxx-small" />
        </Tooltip>
      );
    }
  }

  return null;
};

const PERMISSION_TYPE = {
  copy: "common:copy_for_student",
  edit: "common:drive_student_can_edit",
};

const MediaCardWrapper = React.forwardRef(
  ({
    activeAttachment,
    attachment,
    deleteAttachment,
    renameAttachment,
    showRenameAttachmentOption = true,
    onClickAttachmentCard,
    mode,
    mediaCardSize,
    downloadFromUrl,
    attachmentBottomComponent,
    horizontalCardStyle,
    t,
    getViewModeExtraButtons,
    sourceType,
    portalType,
    updateAttachments,
    attachmentGroup,
    showActionsOnHover,
    showDeleteForSelfInViewMode,
    userId,
    assessmentMode,
    showActionButtons,
    isLockingEnabled,
    updateAttachmentLockState,
    isUserStudent,
    isDownloadEnabled,
    isDeleteEnabled,
    showIconButton,
    showAttachmentAsPost,
    disableActions,
    showWaveformPlayer,
    dropdownIconButtonSize,
    provider = {},
    className,
    isDragAndDropEnabled,
    snapshot,
    iconContainerStyle,
    isPrintEnabled,
    testId,
    dsVersion,
    submissionTemplateAlertArr,
    handleTryAgainClick,
    courseId,
    assessmentId,
    isLTITool,
    isLTIAttachmentPresent,
    showAttachmentDetails,
    alwaysShowActions,
    showAttachmentInfo,
    getExtraDropdownOptions,
    isCustomClassForAudioWaveform,
    isSinglePageAndNewUnitView,
    unitPlanMode,
    reflectionResponseCreatedBy,
    getAttachmentExtraIconButtons,
    showAttachmentExtraIconButtons,
    openLinkInNewTab,
    showNewTabOption,
    getPopupContainer,
    unitCourseId,
    isToddleDoc,
    userType,
    downloadToddleDoc,
    parentType,
    onEditAttachmentClick,
    showEditResponseButtonProps,
    curriculumProgram,
    showAudioCurrentTime,
  }) => {
    const [showMediaModal, setShowMediaModal] = useState(false);
    const [isAudioLoaded, setIsAudioLoaded] = useState(false);
    const [showToddleDoc, setShowToddleDoc] = useState(false);
    const [isHovered, setIsHovered] = useState(false);
    const handleMouseEnter = () => setIsHovered(true);
    const handleMouseLeave = () => setIsHovered(false);
    const setAudioLoaded = () => {
      setIsAudioLoaded(!isAudioLoaded);
    };
    const [showPopover, setShowPopover] = useState(false);
    const [ltiModal, setLtiModal] = useState(false);

    const isToddleDocDownloadWithCommentsEnabled = ACLStore.can(
      "FeatureFlag:EnableDownloadToddleDocWithComments"
    );
    const isToddleDocAttachment =
      _.get(attachment, "type") === "TODDLE_DOC" &&
      isToddleDocDownloadWithCommentsEnabled;

    const {
      options: toddleDocDownloadOptions,
      handleMenuVisibleChange: handleToddleDocDownloadMenuVisibleChange,
      isOwnOptionKey: isToddleDocDownloadOptionKey,
      getIncludeCommentsFromKey: getToddleDocIncludeCommentsFromKey,
    } = useToddleDocDownloadOptions({
      attachment,
      optionKeyPrefix: "mediaCardDownload",
      testIdPrefix: "mediaCard-download",
    });

    const warningIconRef = useRef(null);

    const filterDataExtraParams = useMemo(
      () => ({
        attachmentId: attachment?.attachmentId,
      }),
      [attachment]
    );

    const isDragging = _.get(snapshot, "isDragging", false);
    const permission = _.get(attachment, "metadata.permission", false);
    const permissionText = _.get(PERMISSION_TYPE, permission, "");

    const isWorkbook = _.isEqual(_.get(attachment, "type", ""), "WORKBOOK");
    const uploadProgress = _.get(attachment, "progress", "");

    const extraDropdownOptions = getExtraDropdownOptions?.({ attachment });

    //disable media card click while while pdf is being converted to workbook
    let workbookClickDisabledUntilUpload = false;
    if (isWorkbook && _.isNumber(uploadProgress) && uploadProgress < 100) {
      workbookClickDisabledUntilUpload = true;
    }

    // if reflection response is created by user and single page ui is enabled then remove post option will be visible
    const { isPost } = attachment;
    const canRemoveAttachment =
      isSinglePageAndNewUnitView &&
      isPost &&
      _.isEqual(userId, reflectionResponseCreatedBy);

    const workbookPageCount =
      isWorkbook && _.get(attachment, "metadata.numOfPages", 0);

    const subtitleSuffixComponent = getSubtitleSuffixComponent({
      assessmentMode,
      sourceType,
      attachment,
      t,
    });

    const allowDownload =
      getAllowDownload({
        parentType: attachment.parentType,
        type: attachment.type,
      }) &&
      isDownloadEnabled &&
      isPrintEnabled;

    const isDSV2 = dsVersion === "2.0";

    const onCardActionClick = action => {
      if (isToddleDocAttachment && isToddleDocDownloadOptionKey(action)) {
        downloadToddleDoc({
          attachment,
          shouldAskForSave: true,
          showProgress: mode !== "view",
          includeComments: getToddleDocIncludeCommentsFromKey(action),
        });
        return;
      }
      switch (action) {
        case "delete":
          deleteAttachment(attachmentGroup.id);
          break;
        case "download":
          if (_.isEqual(attachment?.type, "TODDLE_DOC")) {
            downloadToddleDoc({
              attachment,
              shouldAskForSave: true,
              showProgress: mode !== "view",
            });
            break;
          }
          downloadFromUrl({
            attachment,
            shouldAskForSave: true,
            showProgress: mode !== "view",
          });
          break;
        case "rename":
          renameAttachment({
            id: attachment?.attachmentId || attachment?.id,
            name: attachment.name,
          });
          break;
        case "lock": {
          updateAttachmentLockState({
            attachment,
            lockingState: {
              state: ENTITY_LOCKED_STATE,
              scheduledAt: moment(),
            },
          });
          break;
        }
        case "unlock":
          {
            updateAttachmentLockState({
              attachment,
              lockingState: {
                state: ENTITY_UNLOCKED_STATE,
                scheduledAt: moment(),
              },
            });
          }
          break;
        case "submission_details":
          {
            showAttachmentDetails(attachment);
          }
          break;
        case "open_in_new_tab":
          {
            const attachmentUrl = _.get(attachment, "url", "");
            openLinkInNewTab({ url: attachmentUrl });
          }
          break;
        default: {
          const customAction =
            _.find(extraDropdownOptions, { key: action }) || {};

          if (!_.isEmpty(customAction)) {
            customAction?.onClick?.(attachment);
          } else {
            console.warn("do nothing");
          }
        }
      }
    };

    const alignConfig = { offset: [mediaCardSize === "small" ? 6 : 0, 0] };

    const getCardActions = () => {
      const iconContainerClass = classNames({
        [classes.iconCon]: true,
        [classes.iconContainer]: mediaCardSize !== "small",
        [classes.disabled]: disableActions,
      });

      const isEditMode = _.isEqual(mode, "edit");

      const canDeleteAttachment =
        isEditMode ||
        isDeleteEnabled ||
        (showDeleteForSelfInViewMode &&
          _.isEqual(userId, _.get(attachment, "createdBy.id", null))) ||
        canRemoveAttachment;

      const canRenameAttachment = isEditMode && showRenameAttachmentOption;

      const canLockAttachment =
        isLockingEnabled && !isAttachmentLocked({ attachment });

      const canUnlockAttachment =
        isLockingEnabled && isAttachmentLocked({ attachment });

      // Attachment will be lock for student only and teacher will be able to edit it
      const canShowLockingOptions =
        !isUserStudent &&
        _.isEqual(mode, "edit") &&
        !_.includes(attachment?.id, "NEW_");

      const allowLockAttachment = canLockAttachment && canShowLockingOptions;

      const allowUnlockAttachment =
        canUnlockAttachment && canShowLockingOptions;

      const { type: attachmentType } = attachment || {};

      const baseOptions = getOptionsWithIcon({
        t,
        size: isEditMode ? "xx-small" : "xxx-small",
        variant: isEditMode ? "default" : "subtle",
        allowDownload,
        canDeleteAttachment,
        canRenameAttachment,
        allowLockAttachment,
        allowUnlockAttachment,
        disableRename:
          _.get(attachment, "type") === "NOTE" ||
          _.includes(attachment?.id, "NEW"),
        disableActions,
        isLTITool,
        isEditMode,
        showAttachmentInfo,
        extraDropdownOptions,
        canRemoveAttachment,
        attachmentType,
        showNewTabOption,
        isSinglePageAndNewUnitView,
      }).filter(option => option.isVisible);

      const downloadOptionIndex = isToddleDocAttachment
        ? _.findIndex(baseOptions, { key: "download" })
        : -1;
      const options =
        downloadOptionIndex >= 0
          ? [
              ..._.slice(baseOptions, 0, downloadOptionIndex),
              ...toddleDocDownloadOptions,
              ..._.slice(baseOptions, downloadOptionIndex + 1),
            ]
          : baseOptions;

      const renderIconWithTooltip = ({ key, label, icon }) => {
        return (
          <ConditionalWrapper
            type="wrapperAsElement"
            condition={true}
            wrapper={
              isDSV2 ? (
                <Tooltip tooltip={label} dsVersion="2.0" />
              ) : (
                <LinkWithTooltip
                  tooltip={label}
                  placement="topRight"
                  mouseEnterDelay={0.5}
                  alignConfig={alignConfig}
                  tooltipContainerStyle={toolTipStyle}
                  key={key}
                />
              )
            }
          >
            {showIconButton ? (
              <IconButton
                icon={icon}
                _legacy_variant={"plain"}
                variant="neutral"
                type="plain"
                disabled={disableActions}
                onClick={e => {
                  e.stopPropagation();
                  !disableActions && onCardActionClick(key);
                }}
              />
            ) : (
              <div
                className={iconContainerClass}
                onClick={e => {
                  e.stopPropagation();
                  !disableActions && onCardActionClick(key);
                }}
                style={iconContainerStyle}
              >
                {icon}
              </div>
            )}
          </ConditionalWrapper>
        );
      };

      const handlePopOver = () => setShowPopover(prev => !prev);
      if (unitPlanMode === "view" && isSinglePageAndNewUnitView) {
        return;
      }
      if (
        (isEditMode || alwaysShowActions) &&
        !(isSinglePageAndNewUnitView && _.size(options) < 2)
      ) {
        const attachmentOptions = (
          <div className={classes.actionIconContainer}>
            {showAttachmentExtraIconButtons && getAttachmentExtraIconButtons
              ? getAttachmentExtraIconButtons({ attachment })
              : null}
            {_.includes(
              submissionTemplateAlertArr,
              _.get(attachment, "attachmentId", "")
            ) ? (
              <div ref={warningIconRef}>
                <IconButton
                  onClick={e => {
                    handlePopOver();
                    e.stopPropagation();
                  }}
                  size="small"
                  icon={<WarningTriangleFilled variant="critical" />}
                  _legacy_variant="plain"
                  variant="neutral"
                  type="plain"
                />
                {showPopover
                  ? renderRetryPopover({
                      warningIconRef,
                      handlePopOver,
                    })
                  : null}
              </div>
            ) : null}
            {!_.isEmpty(options) && (
              <Dropdown
                getPopupContainer={
                  getPopupContainer
                    ? getPopupContainer
                    : el => {
                        return el;
                      }
                }
                onVisibleChange={
                  isToddleDocAttachment && downloadOptionIndex >= 0
                    ? handleToddleDocDownloadMenuVisibleChange
                    : undefined
                }
                overlay={
                  <DropdownMenu
                    onClick={e => {
                      const action = e.key;
                      onCardActionClick(action);
                    }}
                    options={options}
                    shouldStopPropagation={true}
                    testId={`${testId}-actionMenu`}
                    dsVersion={dsVersion}
                  />
                }
                placement={"bottomRight"}
              >
                <IconButton
                  onClick={e => {
                    e.stopPropagation();
                  }}
                  size={dropdownIconButtonSize}
                  testId={`${testId}-actionMenu`}
                  icon={
                    <DotsVerticalOutlined
                      variant={"subtle"}
                      size={"xx-small"}
                    />
                  }
                  _legacy_variant="plain"
                  variant="neutral"
                  type="plain"
                  dsVersion={dsVersion}
                />
              </Dropdown>
            )}
          </div>
        );
        return attachmentOptions;
      } else {
        return (
          <React.Fragment>
            {getViewModeExtraButtons &&
              getViewModeExtraButtons?.({ attachment, isLTIAttachmentPresent })}
            {_.map(options, option => renderIconWithTooltip(option))}
          </React.Fragment>
        );
      }
    };

    const handleOnClickMediaCard = e => {
      e.stopPropagation();
      if (isDragging) return;
      //disable media card click while while pdf is being converted to workbook
      if (workbookClickDisabledUntilUpload) return;
      if (_.isEqual(sourceType, "OPENING_MESSAGE_RESOURCE")) {
        setShowMediaModal(true);
        return;
      }
      if (isToddleDoc && mode === "edit" && userType === "staff") {
        toggleToddleDoc();
        return;
      }
      if (isLTITool && _.isEmpty(_.get(attachment, "metadata.attachmentId"))) {
        toggleLTIComponent();
        return;
      }
      if (onClickAttachmentCard) {
        onClickAttachmentCard(attachment, attachmentGroup);
      } else {
        if (!showMediaModal) {
          setShowMediaModal(true);
        }
      }
    };

    const toggleToddleDoc = useCallback(
      () => setShowToddleDoc(!showToddleDoc),
      [showToddleDoc]
    );

    const toggleMediaModal = () => setShowMediaModal(!showMediaModal);
    const toggleLTIComponent = () => setLtiModal(!ltiModal);

    const curriculumId = _.get(curriculumProgram, "id");

    const { id, name, mimeType, type, url } = attachment;
    const updatedFileType =
      type === "TODDLE_DOC"
        ? "TODDLE DOC"
        : showAttachmentAsPost && isPost
        ? "POST"
        : type;

    const isTranslationKey = _.isString(name) && _.includes(name, ":");

    let fileName;
    if (isLTITool && (_.isEmpty(name) || isTranslationKey)) {
      // translate name for LTI tools
      fileName = getLtiToolDisplayName(attachment);
    } else if (!_.isEmpty(name) && !isTranslationKey) {
      // use name as-is when already resolved
      fileName = name;
    } else if (type === "AUDIO") {
      // give name "audio" for audio files
      fileName = t("common:audio");
    } else {
      // fallback
      fileName = name ?? "";
    }

    const size =
      attachment?.metadata?.fileSize ?? attachment?.metadata?.size ?? undefined;

    const { thumbUrl, isIcon } = getThumbUrl({
      attachment,
      width: isLTITool ? 256 : 40,
      height: isLTITool ? 256 : 40,
    });

    const isOpeningMessageResource =
      _.isEqual(sourceType, "OPENING_MESSAGE_RESOURCE") ||
      _.isEqual(parentType, "PLANNER_FIELD_OPENING_MESSAGE_RESOURCE");

    const mediaCardFileType =
      isOpeningMessageResource && updatedFileType === "TODDLE DOC"
        ? t("common:document")
        : getMediaCardFileType({ type: updatedFileType, mimeType });

    const fileNameAndSize = (
      <>
        {mediaCardFileType}
        {size > 0 && ` (${formatBytes({ bytes: size, decimals: 1 })})`}
        {permissionText && sourceType === "STUDENT_TEMPLATE" && (
          <>
            <div className={classes.dot}></div>
            {t(permissionText)}
          </>
        )}
      </>
    );

    const containerClasses = classNames({
      [classes.container]: true,
      [classes.containerV2]: isDSV2,
    });
    const getSubtitleText = () => {
      if (isLTITool) {
        return getLtiToolDisplayName(attachment);
      } else if (isWorkbook) {
        if (
          _.isEqual(sourceType, "OPENING_MESSAGE_RESOURCE") ||
          _.isEqual(parentType, "PLANNER_FIELD_OPENING_MESSAGE_RESOURCE")
        ) {
          return "Whiteboard";
        }
        return (
          workbookPageCount &&
          t("common:workbook_page", {
            count: workbookPageCount,
          })
        );
      } else {
        return (
          <div className={classes.subtextContainer}>
            <Tooltip tooltip={fileNameAndSize} showArrow={true}>
              <div className={containerClasses}>{fileNameAndSize}</div>
            </Tooltip>
            {isLockingEnabled ? (
              <LockingStateLabelComponent
                attachment={attachment}
                showDotSeparator
                dsVersion={dsVersion}
              />
            ) : null}
          </div>
        );
      }
    };

    const getHoverSubtitleText = () => {
      return (
        <div className={classes.subtextContainer}>
          <div className={containerClasses}>
            {t("common:view_with_label_lowercase", {
              label: mediaCardFileType,
            })}
          </div>
        </div>
      );
    };

    const subtitleLabel =
      isHovered && isSinglePageAndNewUnitView && unitPlanMode == "view"
        ? getHoverSubtitleText()
        : getSubtitleText();

    const thumbnailIcon = isIcon
      ? getThumbnailIcon({ type, mimeType })
      : undefined;

    const isActiveAttachment = activeAttachment
      ? activeAttachment == _.get(attachmentGroup, "attachmentId") ||
        activeAttachment == id
      : false;
    // we are adding this only for LINK type to show default icon in DS mediaCard
    // we will remove this once we change all attachment cards to DS mediaCard.
    const isDefaultLinkThumbUrl =
      _.isEqual(type, "LINK") &&
      (_.isEqual(_.get(attachment, "thumbUrl"), DEFAULT_LINK_THUMB_URL) ||
        _.includes(
          _.get(attachment, "thumbUrl", ""),
          "/assets/images/linkDefaultImage.png"
        ));

    const isDefaultToddleDocThumbUrl = type === "TODDLE_DOC";
    const style = {
      ..._.get(provider, "draggableProps.style", {}),
      ...(horizontalCardStyle || {}),
      pointerEvents: isDragging ? "auto" : "",
    };

    const dragIconStyle = {
      cursor: isDragging ? "grabbing" : "grab",
    };

    const customClassNames = classNames(className, {
      [classes.dragDropStyle]: isDragAndDropEnabled,
    });

    const audioWaveformClasses = classNames(classes.audioWaveformWrapper, {
      [className]: isCustomClassForAudioWaveform,
    });
    const customType = type === "TODDLE_DOC" ? "TODDLE-DOC" : type;
    const customThumbnailIcon =
      type === "TODDLE_DOC" ? undefined : thumbnailIcon;

    const renderMediaCard = () => (
      <MediaCard
        thumbUrl={
          isIcon || isDefaultLinkThumbUrl || isDefaultToddleDocThumbUrl
            ? undefined
            : thumbUrl
        }
        mimeType={mimeType}
        name={htmlToText(fileName) || t("common:untitled")}
        type={customType}
        icon={customThumbnailIcon}
        className={customClassNames}
        subtext={subtitleLabel}
        onClick={handleOnClickMediaCard}
        cardType={mediaCardSize}
        options={showActionButtons ? getCardActions() : null}
        isActivated={isActiveAttachment}
        bottomElement={attachmentBottomComponent?.()}
        showActionsOnHover={showActionsOnHover}
        url={url ?? thumbUrl}
        subtitleSuffix={subtitleSuffixComponent}
        testId={testId}
        dsVersion={dsVersion}
      />
    );

    const renderRetryPopover = ({ warningIconRef, handlePopOver }) => {
      return (
        <Popover
          isOpen={true}
          align={"center"}
          positions={["bottom"]}
          reposition={true}
          containerStyle={popOverContainerStyle}
          content={({ position, childRect, popoverRect }) => {
            return (
              <ArrowContainer
                position={position}
                childRect={childRect}
                popoverRect={popoverRect}
                arrowColor={colors.white}
                arrowSize={10}
                className="popover-arrow-container"
                arrowClassName="popover-arrow"
              >
                <div className={classes.popUpContainer}>
                  <div className={classes.headerContainer}>
                    <h6>{t("classRoom:submission_warning_popover_header")}</h6>
                    <IconButton
                      onClick={e => {
                        handlePopOver();
                        e.stopPropagation();
                      }}
                      size="small"
                      icon={<CloseOutlined size="xx-small" variant="subtle" />}
                      _legacy_variant="plain"
                      variant="neutral"
                      type="plain"
                    />
                  </div>
                  <div>
                    {t("classRoom:submission_template_not_uploaded_alert_msg", {
                      attachmentName: _.get(attachment, "name", ""),
                    })}
                  </div>
                  <div className={classes.tryAgainContainer}>
                    <Button
                      _legacy_variant="outlined-subtle"
                      variant="neutral"
                      type="outlined"
                      onClick={e => {
                        handleTryAgainClick(
                          _.get(attachment, "attachmentId", "")
                        );
                        e.stopPropagation();
                      }}
                    >
                      {t("common:try_again")}
                    </Button>
                  </div>
                </div>
              </ArrowContainer>
            );
          }}
          childrenRef={{ current: warningIconRef.current }}
          isChildrenRefPassed={true}
        ></Popover>
      );
    };

    return (
      <div
        {...provider.draggableProps}
        {...provider.dragHandleProps}
        ref={provider.innerRef}
        style={style}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        {attachment.type === "AUDIO" && showWaveformPlayer ? (
          <div className={audioWaveformClasses}>
            <AudioWaveformPlayer
              attachment={attachment}
              audioLoaded={setAudioLoaded}
              showCurrentTime={showAudioCurrentTime}
            />
            {showActionButtons ? getCardActions() : null}
          </div>
        ) : isDragAndDropEnabled ? (
          <div
            className={classes.mediaCardWrapperContainer}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
          >
            <div className={classes.dragIconContainerStyle}>
              <DotsSixVerticalOutlined
                className={classes.dragIcon}
                style={dragIconStyle}
              />
            </div>
            {renderMediaCard()}
          </div>
        ) : (
          renderMediaCard()
        )}
        {showMediaModal ? (
          <ModelContent
            isPrintEnabled={isPrintEnabled}
            attachments={[attachment]}
            toggleMediaModal={toggleMediaModal}
            mode={mode}
            sourceType={sourceType}
            portalType={portalType}
            updateAttachments={updateAttachments}
            onWorkbookUpdateSuccessfully={() => toggleMediaModal(false)}
            onEditAttachmentClick={onEditAttachmentClick}
            showEditResponseButtonProps={showEditResponseButtonProps}
          />
        ) : null}
        {showToddleDoc ? (
          <ToddleDoc
            onClose={toggleToddleDoc}
            attachmentId={_.get(attachment, "attachmentId")}
          />
        ) : null}
        {ltiModal ? (
          <LTIComponent
            serviceName={attachment?.metadata?.service}
            toggleLTIComponent={toggleLTIComponent}
            courseId={courseId || unitCourseId}
            resourceLinkId={assessmentId}
            filterDataExtraParams={filterDataExtraParams}
            attachmentContextId={attachment?.metadata?.attachmentContextId}
            curriculumId={curriculumId}
          />
        ) : null}
      </div>
    );
  }
);

MediaCardWrapper.displayName = "MediaCardWrapper";

const mapStateToProps = (state, ownProps) => {
  const { attachment } = ownProps;

  const userInfo = getUserInfo();
  const userId = userInfo.id;

  let mode = _.get(ownProps, "mode", "edit");
  const unitPlanMode = _.get(state, "unitPlans.unitPlanMode");

  const isLockingEnabled = _.get(ownProps, "isLockingEnabled", false);

  const userType = getUserType(state);

  const isUserStudent = _.isEqual(userType, STUDENT);

  if (isLockingEnabled && isAttachmentLocked({ attachment }) && isUserStudent) {
    mode = "view";
  }
  const isLTITool =
    _.isEqual(_.get(attachment, "attachments[0].type", ""), "LINK") &&
    _.isEqual(
      _.get(attachment, "attachments[0].metadata.type", ""),
      "LTI_TOOL"
    );

  const isToddleDoc = _.isEqual(
    _.get(attachment, "attachments[0].type", ""),
    "TODDLE_DOC"
  );

  const updatedAttachement = getFilteredAttachment({
    attachment,
    isLTITool,
  });

  return {
    attachment: updatedAttachement,
    attachmentGroup: attachment,
    userId,
    mode,
    isUserStudent,
    isLTITool,
    unitPlanMode,
    isToddleDoc,
    userType,
  };
};

MediaCardWrapper.propTypes = {
  mediaCardSize: PropTypes.string,
  showActionsOnHover: PropTypes.bool,
  showDeleteForSelfInViewMode: PropTypes.bool,
  isLockingEnabled: PropTypes.bool,
  isDownloadEnabled: PropTypes.bool,
  isDeleteEnabled: PropTypes.bool,
  showAttachmentAsPost: PropTypes.bool,
  horizontalCardStyle: PropTypes.object,
  showWaveformPlayer: PropTypes.bool,
  dropdownIconButtonSize: PropTypes.string,
  iconContainerStyle: PropTypes.object,
  isPrintEnabled: PropTypes.bool,
  testId: PropTypes.string,
  dsVersion: PropTypes.string,
  courseId: PropTypes.string,
  assessmentId: PropTypes.string,
  isCustomClassForAudioWaveform: PropTypes.bool,
  showAudioCurrentTime: PropTypes.bool,
};

MediaCardWrapper.defaultProps = {
  mediaCardSize: "medium",
  showActionsOnHover: false,
  showDeleteForSelfInViewMode: false,
  showActionButtons: true,
  isLockingEnabled: false,
  isDownloadEnabled: true,
  isDeleteEnabled: false,
  showAttachmentAsPost: false,
  horizontalCardStyle: {},
  showWaveformPlayer: false,
  dropdownIconButtonSize: "medium",
  iconContainerStyle: {},
  isPrintEnabled: true,
  testId: "appComponents-mediaCardWrapper",
  dsVersion: "1.0",
  isCustomClassForAudioWaveform: false,
  showAudioCurrentTime: false,
};

const mapActionCreators = {
  downloadFromUrl,
  openLinkInNewTab,
  downloadToddleDoc,
};

export default compose(
  I18nHOC({ resource: ["common"] }),
  connect(mapStateToProps, mapActionCreators, null, { withRef: true })
)(MediaCardWrapper);
